/**
 * Currency utility functions for South African Rand (ZAR)
 */

/**
 * Formats a number as South African Rand currency
 * @param {number|string} amount - The amount to format
 * @param {boolean} showDecimals - Whether to show decimal places (default: true)
 * @returns {string} Formatted currency string (e.g., "R 1 234.56")
 */
export const formatZAR = (amount, showDecimals = true) => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numericAmount) || numericAmount === null || numericAmount === undefined) {
    return 'R 0';
  }

  const formatter = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  });

  // Format and replace R with "R " for proper spacing
  let formatted = formatter.format(numericAmount);
  
  // Ensure proper spacing: "R 1 234.56" instead of "R1,234.56"
  formatted = formatted.replace('R', 'R ');
  
  // Replace commas with spaces for South African formatting
  formatted = formatted.replace(/,/g, ' ');
  
  return formatted;
};

/**
 * Formats a number as ZAR without decimals
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted currency string without decimals (e.g., "R 1 234")
 */
export const formatZARCompact = (amount) => {
  return formatZAR(amount, false);
};

/**
 * Parses a ZAR formatted string back to a number
 * @param {string} zarString - ZAR formatted string (e.g., "R 1 234.56")
 * @returns {number} Parsed number value
 */
export const parseZAR = (zarString) => {
  if (!zarString || typeof zarString !== 'string') {
    return 0;
  }
  
  // Remove R symbol and spaces, then parse
  const cleanString = zarString.replace(/[R\s]/g, '');
  const numericValue = parseFloat(cleanString);
  
  return isNaN(numericValue) ? 0 : numericValue;
};

/**
 * Validates if a string is a valid ZAR amount
 * @param {string} value - Value to validate
 * @returns {boolean} True if valid ZAR amount
 */
export const isValidZARAmount = (value) => {
  if (!value) return true; // Empty is valid (optional field)
  
  // Allow formats like: 1234.56, 1 234.56, R1234.56, R 1 234.56
  const zarPattern = /^R?\s*\d{1,3}(\s\d{3})*(\.\d{1,2})?$/;
  const simplePattern = /^\d+(\.\d{1,2})?$/;
  
  return zarPattern.test(value.trim()) || simplePattern.test(value.trim());
};

export default {
  formatZAR,
  formatZARCompact,
  parseZAR,
  isValidZARAmount
};